// Tell EVERYONE ELSE their cache is stale after a WRITE.
//
// ── The gap this closes ────────────────────────────────────────────────────
//
// `Users.SinkData = 1` is the server's "your structure changed, pull it again"
// flag: runSyncIfNeeded (syncService.js) does a full pull when it is set and
// then clears it. For years the app only ever CLEARED that flag — nothing set
// it. So a rename made on one phone reached the server and stayed invisible on
// every other phone with access to the same device, indefinitely.
// core/db/localPatch fixes the cache of the phone that made the change; THIS
// file marks everybody else, so their next launch (or return to the
// foreground — see AppContext) pulls the new names.
//
// ── How the audience is resolved ──────────────────────────────────────────
//
// There is no endpoint that lists the users of a location or a room (probed
// 2026-09-05: every likely name 404s), and resolving a device's users on the
// client would be one extra round trip per write. So the audience is a SQL
// subquery inside the `where` of the generic UpdateUsers endpoint, which the
// backend interpolates into the statement as raw text (functions.js in the api
// repo says so in as many words). One call, exact audience, no new backend.
//
//   device  -> UserDeviceMapping for that install row + DevicesInstalled.MasterUserId
//              (the owner often has NO mapping row — see getUsersForDevice in api.js)
//   room    -> LocationRoomsMapping for that RoomID
//   location-> UserLocationMapping for that LocationID
//
// ⚠️ THE ACTOR IS EXCLUDED ONLY WHEN THIS DEVICE HAS A CACHE. On a phone their
// cache was patched in place by localPatch (or rebuilt by resyncSelf), and
// marking them would force a pointless ten-call full sync on their next launch.
// On the DESKTOP there is no cache (dbSupported() is native-only), so nothing
// was patched — and excluding them left THEIR PHONE stale: a device assigned
// from the desktop never appeared on the assigner's own phone (2026-09-29,
// Room Door Lock 1085864 in Testing, user 1, SinkData stayed 0). So off a phone
// the actor is marked like everyone else. `actor` is the SIGNED-IN user, never
// the view-as user.
//
// ⚠️ MARK AFTER THE WRITE RESOLVES, NEVER BEFORE. A rename that fails must not
// wake up the whole household for nothing — and the subquery reads the mapping
// tables, which the write does not touch, so "after" still sees the right rows.
// The one write that DOES change the audience (unassign clears MasterUserId)
// deletes the owner's mapping explicitly, and that delete marks the owner.
//
// Every function is best-effort and never throws — a user left unmarked is
// stale, which is what they already were; a marker that broke the rename would
// be worse than the bug.

import { post } from '../api/api';
import { loadUserData } from '../utils/storage';
import { fullSync } from './syncService';
import { dbSupported } from './schema';

// True when THIS device holds a local cache that a write can patch or rebuild.
const selfHandledHere = () => { try { return !!dbSupported(); } catch (e) { return false; } };

/** The signed-in user's id, or 0 when there is no session. */
export const actorId = () => {
  const u = loadUserData();
  const id = u && u[0] ? Number(u[0].UserId) : 0;
  return Number.isFinite(id) ? id : 0;
};

const num = (v) => Number(v) || 0;
// Unique numbers are alphanumeric; anything else is stripped rather than quoted
// because `where` is raw SQL on the server.
const uno = (v) => String(v ?? '').replace(/[^A-Za-z0-9]/g, '');

const sendMark = async (w, what) => {
  try {
    await post('UpdateUsers', { data: { SinkData: '1' }, where: w });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`[SinkData] could not mark users of ${what}:`, (e && e.message) || e);
  }
};

/*
 * ⚠️ COALESCED. A reorder writes one row per device — twenty writes, and each
 * would mark the same audience: twenty identical UpdateUsers calls. The FIRST
 * mark is sent at once; the same `where` asked for again within
 * MARK_COALESCE_MS shares that call (and its promise). Marking is idempotent,
 * so nothing is lost by sending it once.
 */
const MARK_COALESCE_MS = 800;
const recent = new Map(); // where -> { at, promise }

/** Tests only: forget recent marks so each test sends its own. */
export const resetMarkCoalescing = () => recent.clear();

const mark = (where, what, { includeActor = false } = {}) => {
  const me = !includeActor && selfHandledHere() ? actorId() : 0;
  const w = me ? `(${where}) AND UserId <> ${me}` : `(${where})`;
  const now = Date.now();
  const hit = recent.get(w);
  if (hit && now - hit.at < MARK_COALESCE_MS) return hit.promise;
  const promise = sendMark(w, what);
  recent.set(w, { at: now, promise });
  return promise;
};

/** Everyone with access to a device (by UniqueNumber) — after a device or button rename, a re-home. */
export const markUsersOfDevice = (uniqueNumber) => {
  const u = uno(uniqueNumber);
  if (!u) return Promise.resolve();
  return mark(
    `UserId IN (SELECT m.UserId FROM UserDeviceMapping m`
    + ` INNER JOIN DevicesInstalled d ON d.ID = m.DeviceInstalledID`
    + ` WHERE d.UniqueNumber = '${u}')`
    + ` OR UserId IN (SELECT MasterUserId FROM DevicesInstalled WHERE UniqueNumber = '${u}')`,
    `device ${u}`,
  );
};

/** Everyone mapped to a room — after a room rename. */
export const markUsersOfRoom = (roomId) => {
  const id = num(roomId);
  if (!id) return Promise.resolve();
  return mark(`UserId IN (SELECT UserId FROM LocationRoomsMapping WHERE RoomID = ${id})`, `room ${id}`);
};

/** Everyone mapped to a location — after a location rename. */
export const markUsersOfLocation = (locationId) => {
  const id = num(locationId);
  if (!id) return Promise.resolve();
  return mark(`UserId IN (SELECT UserId FROM UserLocationMapping WHERE LocationID = ${id})`, `location ${id}`);
};

// ── The actor's own cache, when a ROW appears or vanishes ─────────────────
// localPatch rewrites names in place; it cannot add or drop rows. So when a
// mapping is written for the signed-in user, their cache is rebuilt by
// fullSync. Coalesced, because one "add device" writes three mappings
// (location, room, device) back to back and that must not be three ten-call
// syncs. A flow that needs the rows BEFORE it returns — the screen after
// "Device Added" reads the room local-first — awaits resyncSelf() instead.
let selfTimer = null;
export const resyncSelfSoon = (userId) => {
  const id = num(userId) || actorId();
  if (!id) return;
  if (selfTimer) clearTimeout(selfTimer);
  selfTimer = setTimeout(() => {
    selfTimer = null;
    fullSync(id).catch(() => {});
  }, 1500);
};

/** Rebuild the signed-in user's cache now, cancelling any coalesced one. Never throws. */
export const resyncSelf = async (userId) => {
  const id = num(userId) || actorId();
  if (selfTimer) { clearTimeout(selfTimer); selfTimer = null; }
  if (!id) return false;
  return fullSync(id).catch(() => false);
};

/**
 * The user's OWN flag, ALWAYS — even the actor on a phone. For writes that are
 * about that one user (a favourite, their own order of devices or locations):
 * this phone's cache is patched or rebuilt by the caller, but the same user's
 * OTHER phone (or their phone, after a change on the desktop) must still pull.
 * Costs this phone one quick sync at its next launch — the price of no stale
 * device anywhere.
 */
export const markUserAlways = (userId) => {
  const id = num(userId);
  if (!id) return Promise.resolve();
  return mark(`UserId = ${id}`, `user ${id}`, { includeActor: true });
};

/**
 * Everyone with access to a BLE lamp parent/group — after it is renamed,
 * moved to another room or deleted. It is a DevicesInstalled row (DeviceID
 * 100) reached through BleNode.DevicesInstalledId.
 */
export const markUsersOfBleNode = (nodeId) => {
  const id = num(nodeId);
  if (!id) return Promise.resolve();
  return mark(
    `UserId IN (SELECT m.UserId FROM UserDeviceMapping m`
    + ` INNER JOIN BleNode n ON n.DevicesInstalledId = m.DeviceInstalledID WHERE n.NodeId = ${id})`
    + ` OR UserId IN (SELECT d.MasterUserId FROM DevicesInstalled d`
    + ` INNER JOIN BleNode n ON n.DevicesInstalledId = d.ID WHERE n.NodeId = ${id})`,
    `BLE node ${id}`,
  );
};

/**
 * One named user — after a mapping row is added or removed FOR them (a device
 * granted or revoked, a location or room shared). If that user is the actor,
 * a server flag would be ignored (they are excluded above), so their own cache
 * is rebuilt instead.
 */
export const markUser = async (userId) => {
  const id = num(userId);
  if (!id) return;
  if (id === actorId() && selfHandledHere()) {
    resyncSelfSoon(id);
    return;
  }
  await mark(`UserId = ${id}`, `user ${id}`);
};
